import { useAuth } from "@dl/web-core-js/auth";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Button, Card, Field, Input, toast } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Info, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber, refusalMessage, type PartDTO, type PartGridDTO } from "../../api/manufacturing";
import { SubcontractComponentClient, type SubcontractComponentDTO } from "../../api/replenishment";
import { PartPicker } from "./PartPicker";
import { requiredNumber } from "./PartRelatedTabs";

/**
 * A subcontracted part's bill: what the subcontractor uses of ours to make one.
 *
 * <p>VIZU laminates our raw board with our paper and sends back finished
 * panels. We buy the lamination; the board and paper were already ours,
 * waiting in VIZU's warehouse. When the finished panel is received against
 * the order, each line here is consumed from the supplier's subcontract
 * warehouse — quantity × panels received — on the same receipt, and reversing
 * the receipt gives them back.</p>
 *
 * <p>Nothing happens for a supplier with no subcontract warehouse, so an
 * ordinary purchased part can have an empty bill forever.</p>
 */
export function PartSubcontractTab({ part, readOnly }: { part: PartDTO; readOnly: boolean }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const queryClient = useQueryClient();
  const client = useMemo(() => new SubcontractComponentClient(http), [http]);
  const key = ["purchasing", "subcontract-component", part.id] as const;

  const rows = useQuery({
    queryKey: key,
    queryFn: async () => (await client.find({ page: 0, size: 500, filters: { part_id: [String(part.id)] } })).content,
  });

  const [picked, setPicked] = useState<PartGridDTO | null>(null);
  const [quantity, setQuantity] = useState("1");

  const add = useMutation({
    mutationFn: () =>
      client.create({ id: 0, part_id: part.id, component_part_id: picked?.id ?? 0, quantity: Number(quantity), note: null }),
    onSuccess: async () => {
      setPicked(null);
      setQuantity("1");
      await queryClient.invalidateQueries({ queryKey: key });
      toast.success(t("subcontract.added", "Added to the subcontract bill"));
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const cols = useMemo(() => {
    const c = defineColumns<SubcontractComponentDTO>();
    return [
      c.text("component_part_number", (row) => row.component_part_number ?? "", { header: t("subcontract.component", "Component"), priority: 1 }),
      c.text("component_part_name", (row) => row.component_part_name ?? "", { header: t("subcontract.name", "Name"), priority: 2 }),
      c.text("quantity", (row) => formatNumber(row.quantity), { header: t("subcontract.quantity", "Per unit received"), filter: "none", priority: 1 }),
      c.text("component_unit_code", (row) => row.component_unit_code ?? "", { header: t("subcontract.unit", "Unit"), filter: "none" }),
      c.text("note", (row) => row.note ?? "", { headerKey: "column.note", filter: "none" }),
    ];
  }, [t]);

  return (
    <div className="flex flex-col gap-4 p-4 lg:p-6">
      <Card className="flex gap-3 p-4">
        <Info className="mt-0.5 size-4 shrink-0 text-info" />
        <p className="text-[12px] leading-relaxed text-ink-muted">
          {t(
            "subcontract.explain",
            "What the subcontractor uses of ours to make one {{part}}. When it is received from a supplier with a subcontract warehouse (VIZU, DELTA), each line is consumed from that warehouse (quantity × units received) on the same receipt.",
            { part: part.number },
          )}
        </p>
      </Card>
      {!readOnly && (
        <Card className="flex flex-wrap items-end gap-3 p-4">
          <div className="min-w-64 flex-1">
            <p className="mb-1 text-[12px] font-medium text-ink">{t("subcontract.add", "Add a component")}</p>
            {picked ? (
              <div className="flex h-9 items-center gap-2 rounded-md border border-line bg-surface-sunk px-3 text-[13px]">
                <span className="font-medium">{picked.number}</span>
                <span className="min-w-0 flex-1 truncate text-ink-muted">{picked.name}</span>
                <button type="button" className="text-[12px] text-accent-strong" onClick={() => setPicked(null)}>
                  {t("button.change", "Change")}
                </button>
              </div>
            ) : (
              <PartPicker
                onPick={setPicked}
                exclude={[part.id, ...(rows.data ?? []).map((row) => row.component_part_id)]}
                placeholder={t("subcontract.find", "Find the board, paper or film it uses…")}
              />
            )}
          </div>
          <Field label={t("subcontract.quantity", "Per unit received")} htmlFor="sc-quantity" className="w-40">
            <Input id="sc-quantity" type="number" min={0} step="any" value={quantity} onChange={(event) => setQuantity(event.target.value)} />
          </Field>
          <Button variant="primary" disabled={!picked || !(Number(quantity) > 0)} loading={add.isPending} onClick={() => add.mutate()}>
            <Plus className="size-4" />
            {t("button.add", "Add")}
          </Button>
        </Card>
      )}
      <DataTable<SubcontractComponentDTO>
        data={rows.data ?? []}
        columns={cols}
        getRowId={(row) => String(row.id)}
        loading={rows.isLoading}
        editor={
          readOnly
            ? undefined
            : {
                fields: {
                  quantity: {
                    kind: "number",
                    get: (row) => row.quantity,
                    set: (row, value) => ({ ...row, quantity: requiredNumber(value) }),
                    required: true,
                  },
                  note: {
                    kind: "text",
                    get: (row) => row.note,
                    set: (row, value) => ({ ...row, note: value === "" ? null : value }),
                  },
                },
                validate: (row) => (row.quantity > 0 ? null : { quantity: t("subcontract.quantity-positive", "Must be above zero.") }),
                onSave: async (row) => {
                  try {
                    await client.update(row);
                  } catch (failure) {
                    throw new Error(refusalMessage(failure, t("error.generic", "Something went wrong.")));
                  }
                  await queryClient.invalidateQueries({ queryKey: key });
                  toast.success(t("notification.saved", "Saved"));
                },
                onDelete: async (row) => {
                  try {
                    await client.remove(row.id);
                  } catch (failure) {
                    throw new Error(refusalMessage(failure, t("error.generic", "Something went wrong.")));
                  }
                  await queryClient.invalidateQueries({ queryKey: key });
                  toast.success(t("notification.deleted", "Deleted"));
                },
                deleteConfirm: (row) => ({
                  title: t("subcontract.remove", "Remove {{part}} from the bill?", { part: row.component_part_number ?? "" }),
                }),
              }
        }
        emptyMessage={t("subcontract.empty", "Nothing: receiving this part consumes nothing of ours.")}
      />
    </div>
  );
}
